/**
 * The opening band's surface and its vertical rhythm.
 *
 * Its own module for the reason `pageIntroInk` and `hoverSpring` are: a file
 * that exports a component may not also export a value without breaking fast
 * refresh. It is also the one place the two layouts have to agree — they are
 * the same band wearing different clothes, and a page with cars that stood
 * 24px taller than a page without would make them read as two components.
 *
 * ## The one breakpoint everything here turns on
 *
 * Below `md` the band is not a stage at all. It is a title block on the page's
 * own surface: trail, eyebrow, headline, a clamped lede and the proof rail,
 * and then the page's actual content. From `md` up it is exactly the
 * cinematic band it has always been.
 *
 * That is not a smaller version of the same idea, it is a different answer to
 * a different question, and the measurements are why. On a 390x844 phone the
 * stage was costing between 554px and 732px before a visitor could reach what
 * they came for — the catalogue, the calculator, the form — which put the
 * first car card 1.07 screens down /cars and the first field 1.47 screens down
 * /tax-calculator. A stage is a composition: it needs a car to the right of
 * the copy, room under the headline for a lede that sets on two lines, and a
 * floor wide enough for three claims side by side. A phone has none of those,
 * so what it actually rendered was the stage's *height* without any of the
 * stage's *composition* — full-width copy over a dimmed car, six lines of
 * lede, and three stacked rows of proof in a glass pane with nothing behind
 * it to frost.
 *
 * `md` rather than `sm` because 900px is the width at which the stage's own
 * pieces start working: the copy column (`COPY_WIDTH` in PageIntroFeature)
 * and the car's half of the band both begin there, and the slideshow has
 * always treated everything below `md` as one full-width case. Splitting the
 * band at `sm` would have invented a third layout that no other part of the
 * band knows about.
 *
 * Every switch is a CSS breakpoint and never a `useMediaQuery`. The site is
 * prerendered with `ssr: false` (react-router.config.ts), so a JavaScript
 * branch would bake one width's layout into the static HTML and then swap it
 * on hydration — a flash and a layout shift on exactly the devices this
 * change exists for.
 */

/**
 * The band's surface.
 *
 * From `md` up: the site's one inverse stage.
 *
 * This used to be two colours — a mid dusty rose in light mode, a slate in
 * dark — and that was the whole problem. Every other inverse surface on the
 * site is the near-black the home hero stands on (`HeroStage`, `#0A0A0C`) and
 * holds it in *both* schemes, which is why `aurora` in `theme/patterns.ts`
 * hard-codes its reds rather than reading them from the palette. This band was
 * the only surface inventing a hue of its own, so it read as a different
 * product from the page above and below it however carefully it was tuned.
 *
 * One value, no scheme branch. A mid-tone needs a scheme branch because a mid
 * dusty rose is wrong at night; a stage does not, because a stage is the
 * absence of a surface rather than a surface.
 *
 * The colour the band does have comes from `aurora`, which is painted under
 * the cars by the slideshow: brand-red fields off opposite corners and a cool
 * lift through the middle. That is a direction of light rather than a tint,
 * which is what the old flat fill was reaching for and could not get — a
 * single hex is the same value everywhere, so it cannot read as depth.
 *
 * Below `md`: the page's own background, which *does* follow the scheme,
 * because here the band is a title block rather than a stage. The slideshow
 * is not painted at that width (see PageIntroSlideshow), and a near-black
 * rectangle with no car in it is not a stage — it is a black rectangle. It is
 * also where the two layouts converge: a compact feature band and a compact
 * plain band are the same object, which is the point `PageIntroPlain` has
 * always been making about the two sharing one rhythm.
 *
 * The scrim ink stays here beside the surface, because the scrim is mixed from
 * the surface and the two going out of step is what produced the band's
 * earlier failures in both directions. It is harmless below `md`, where
 * nothing reads it.
 *
 * Space-separated channels, so a gradient can take them with its own alpha,
 * the way `theme/patterns.ts` takes the palette's `*Channel` variables.
 */
export const BAND_SURFACE = {
  bgcolor: { xs: 'background.default', md: '#0A0A0C' },
  // Matches the `md` `bgcolor` exactly. Anything else and the scrim reads as a
  // cast laid over the stage instead of the stage deepening — the mismatch
  // between a rose band and a near-black scrim is what used to turn the
  // headline's corner muddy while the car's corner stayed pink.
  '--kabex-intro-scrim': '10 10 12',
};

/**
 * What closes the band off below `md`.
 *
 * On the stage the band ends because the colour ends; there is nothing to
 * draw. On the page's own surface there is no such edge, and without a rule
 * the title block and the first content band run together into one
 * undifferentiated column — which is the failure mode of every "we made it
 * compact" title bar. `PageIntroPlain` has always drawn this rule at every
 * width for the same reason; this is the feature band borrowing it for the
 * widths where it is also a plain band.
 */
export const BAND_EDGE = {
  borderBottom: { xs: 1, md: 0 },
  borderColor: 'divider',
};

/**
 * The band's padding, on the theme's 4px unit.
 *
 * At `md` the ceiling came down from 80px to 64px. The old figure was set when
 * the band's only content was a column of type at the top left, and it read as
 * dead space once the composition acquired a floor: a band that is 80px empty
 * at the top and holds a trust strip at the bottom is weighted the wrong way
 * round. The floor is smaller than the ceiling on purpose — the strip has its
 * own internal padding, so the gap under it only has to clear the strip, not
 * carry the band.
 *
 * Below `md` both figures are a third of that, and the reasoning inverts.
 * There is no composition to weight, because there is nothing in the band but
 * type: the padding's only job is to keep the trail clear of the header above
 * it and the rail clear of the rule below it. 80px of air over a title block
 * is not generosity on a 390px screen, it is a third of the first screenful
 * spent on nothing — and it was paid twice, once here and once in `pb`.
 */
export const BAND_PADDING = {
  pt: { xs: 7, md: 16 },
  pb: { xs: 8, md: 14 },
};

/**
 * The gap between the copy block and the proof points.
 *
 * At `md` it is wider than any gap inside the copy, which is the whole job:
 * the strip is a separate claim about the company, not the last line of the
 * page's pitch, and at a smaller gap it started reading as a fourth paragraph
 * of the lede.
 *
 * Below `md` that distinction is carried by shape instead of by distance — the
 * proof points are a rail of bordered chips down there, which cannot be
 * mistaken for a paragraph however close it sits. So the gap only has to
 * separate, and 24px separates.
 */
export const STRIP_GAP = { xs: 6, md: 14 };
